import Cookies, { CookieAttributes } from 'js-cookie';
import { NextPageContext } from 'next';
import { CookieManagerConfig, ServerCookieOptions } from './cookies.types';
import { TRANSITION_SOURCE_BLOCK_COOKIE_TTL_MS } from './cookies.constants';
import { TransitionSourceBlockEnum } from '@/api/types/ads.types';
import { COOKIES } from '@/constants/common';

export function readCookieValueFromHeader(
  cookieHeader: string,
  cookieName: string
): string | undefined {
  for (const part of cookieHeader.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name !== cookieName) continue;
    const raw = rest.join('=');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return undefined;
}

export function getCookieFromRequestContext(
  context: NextPageContext,
  cookieName: string
): string | undefined {
  const cookieHeader = context.req?.headers.cookie;
  if (!cookieHeader) return undefined;
  return readCookieValueFromHeader(cookieHeader, cookieName);
}

export function appendServerSetCookie(
  context: NextPageContext,
  cookieName: string,
  cookieValue: string,
  options: ServerCookieOptions = {}
): void {
  if (!context.res) return;
  let cookieString = `${encodeURIComponent(cookieName)}=${encodeURIComponent(
    cookieValue
  )}`;
  const { expires, maxAge, domain, path, sameSite, secure } = options;
  if (expires) cookieString += `; Expires=${expires.toUTCString()}`;
  if (typeof maxAge === 'number') cookieString += `; Max-Age=${maxAge}`;
  if (domain) cookieString += `; Domain=${domain}`;
  if (path) cookieString += `; Path=${path}`;
  if (sameSite) cookieString += `; SameSite=${sameSite}`;
  if (secure) cookieString += '; Secure';
  const setCookieHeader = context.res.getHeader('Set-Cookie');
  const normalized = setCookieHeader
    ? Array.isArray(setCookieHeader)
      ? setCookieHeader
      : [String(setCookieHeader)]
    : [];
  context.res.setHeader('Set-Cookie', [...normalized, cookieString]);
}

export function createCookieManager<T extends string>({
  key,
  clientOptions = {},
  serverOptions = {},
}: CookieManagerConfig) {
  return {
    get(context?: NextPageContext): T | undefined {
      const rawValue = context
        ? getCookieFromRequestContext(context, key)
        : Cookies.get(key);
      return rawValue ? (rawValue as T) : undefined;
    },
    set(value: T, options: CookieAttributes = {}): void {
      Cookies.set(key, value, { ...clientOptions, ...options });
    },
    clear(context?: NextPageContext): void {
      if (context?.res) {
        appendServerSetCookie(context, key, '', {
          ...serverOptions,
          maxAge: 0,
          expires: new Date(0),
        });
        return;
      }
      Cookies.remove(key, clientOptions);
    },
  };
}

export const getTransitionSourceBlockCookie = (legacyId: string) =>
  createCookieManager<TransitionSourceBlockEnum>({
    key: `${COOKIES.transitionSourceBlock}_${legacyId}`,
    clientOptions: {
      path: '/',
      sameSite: 'Lax',
      expires: new Date(Date.now() + TRANSITION_SOURCE_BLOCK_COOKIE_TTL_MS),
    },
    serverOptions: {
      path: '/',
      sameSite: 'Lax',
      maxAge: TRANSITION_SOURCE_BLOCK_COOKIE_TTL_MS / 1000,
    },
  });

export const appBannerShowCookie = createCookieManager<'true'>({
  key: COOKIES.appBannerShow,
  clientOptions: {
    expires: 7,
    path: '/',
  },
});
